import { useAppDispatch, useAppSelector } from "features/config/hooks";
import { updateCancelledStatus } from "features/stock-ordering/core/stock-ordering.params";
import { updateOrderCancelled } from "../slices/update-order-cancelled.slice";
import {
  ActionHeader,
  BannerIndicator,
  CancelAndConfirm,
  useActionAvailability,
} from "./stock-ordering-utils";
import {
  closeMessageModal,
  openMessageModal,
} from "features/shared/presentation/slices/message-modal.slice";
import { useParams } from "react-router-dom";
import { selectGetProductData } from "../slices/get-product-data.slice";
import { GetProductDataModel } from "features/stock-ordering/core/domain/get-product-data.model";
import dayjs from "dayjs";
import { MaterialDateInput } from "features/shared/presentation/components";
import { useCallback, useState } from "react";
import { stockInitialOrderReview } from "../slices/stock-initial-order-review.slice";
import { UploadMultipleImageModal } from "../modals/upload-multiple-image.modal";
import {
  closeModalScroll,
  toggleModalScroll,
} from "../slices/modalScrollSlice.slice";
import { CheckCircle, CircleCheck } from "lucide-react";

interface SupplierViewOrderProps {
  onClose: () => void;
}

interface ActionApprovalProps {
  primaryTitle?: string;
  secondaryTitle?: string;
  name: string;
  hasAccess: boolean;
  actionButtonVisibility?: boolean;
  order_information?: GetProductDataModel["order_information"] | undefined;
  handleCancelledOrder?: () => void;
  handleConfirm: (comDeliveryDate?: string | null | undefined) => void;
  customButtons?: {
    label: string;
    onClick: () => void;
    position?: "start" | "middle" | "end";
    disabled?: boolean;
  }[];
  fileData?: {
    files: File[] | null;
    handleRemoveFile: () => void;
    handleOpenFileModal: () => void;
  };
  hasAccessBannerIndicatorSettings?: BannerIndicator;
  noAccessBannerIndicatorSettings?: BannerIndicator;
}

interface BannerIndicator {
  visible: boolean;
  content: string;
  textColor: string;
  bgColor: string;
}

export function StockOrderProcessNewOrder(props: SupplierViewOrderProps) {
  const dispatch = useAppDispatch();
  const { orderId } = useParams();

  const [uploadedPayment, setUploadedFile] = useState<File[] | null>(null);

  const [openUploadModal, setOpenUploadModal] = useState(false);

  /**
   * Selector for fetching data
   */
  const getProductDataState = useAppSelector(selectGetProductData);

  /**
   * desstructure product data
   */
  const { order_information, product_batch_data } =
    getProductDataState.data ?? {};

  /**
   * Checks every entries of batches if there is existing delivery date
   */
  const checkBatchDelivery =
    product_batch_data?.length !== 0 &&
    product_batch_data?.every((batch) => batch.delivery_date !== null);

  /**
   * Handles the cancellation of order
   * Tiggered when clicked yes on message modal
   */

  const handleCancelledOrder = () => {
    if (orderId) {
      dispatch(
        openMessageModal({
          message: "Are you sure you want to cancel the order?",
          buttons: [
            {
              text: "Yes",
              color: "#CC5801",
              onClick: () => {
                const cancelParameter: updateCancelledStatus = {
                  id: orderId,
                  remarks: "",
                };
                dispatch(updateOrderCancelled(cancelParameter));

                props.onClose();
                dispatch(closeMessageModal());
              },
            },

            {
              color: "#22201A",
              text: "No",
              onClick: () => {
                dispatch(closeMessageModal());
              },
            },
          ],
        })
      );
    }
  };

  /**
   * Updates the approve status on database
   * @param type manager or procurement
   * @returns none
   */
  const handleConfirmOrder =
    (
      type:
        | "manager"
        | "procurement"
        | "commit"
        | "supplier-approve"
        | "multim-upload-payment"
        | "supplier-multim-approval"
        | "supplier-multim-reject"
    ) =>
    (comDeliveryDate?: string | null | undefined) => {
      dispatch(
        openMessageModal({
          useHtml: true,
          message: `<b>Are you sure you want to approve this?</b><br><br>&nbsp;&nbsp;&nbsp;&nbsp;Once approved, the request will be finalized and no further changes can be made. Please review all the details carefully before proceeding, as this action is permanent and cannot be undone.`,
          buttons: [
            {
              color: "#22201A",
              text: "Go back",
              onClick: () => {
                dispatch(closeMessageModal());
              },
            },
            {
              color: "#CC5801",
              text: "I Confirm",
              onClick: () => {
                dispatch(
                  stockInitialOrderReview({
                    orderId: orderId,
                    type: type,

                    data: {
                      ...(type === "multim-upload-payment" && {
                        uploadedPayment: uploadedPayment?.[0],
                      }),

                      ...(type === "commit" && {
                        commitedDelivery:
                          comDeliveryDate && dayjs(comDeliveryDate).isValid()
                            ? dayjs(comDeliveryDate).toISOString()
                            : undefined,
                      }),
                    },
                  })
                );

                dispatch(closeMessageModal());
                props.onClose();
              },
            },
          ],
        })
      );
    };

  const handleSetFile = (file: File[] | null) => setUploadedFile(file);

  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null);
  }, [uploadedPayment]);

  const handleOpenFileModal = useCallback(() => {
    setOpenUploadModal(true);
    dispatch(toggleModalScroll());
  }, [openUploadModal]);

  /**
   * Hooks for availability
   */
  const managerAccess = useActionAvailability(3).permission;
  const supplierApproval = useActionAvailability(7).permission;
  const procurementAccess = useActionAvailability(2).permission;
  const supplierAccess = useActionAvailability(5).permission;
  const actionAvailableAccess = useActionAvailability().actionAvailable;

  /**
   * manager approval config
   */
  const managerApprovalSettings = {
    primaryTitle: "Store Manager Approval",
    secondaryTitle:
      "Approval from the store manager is necessary to validate and authorize this action before proceeding.",
    name: "manager-approval-config",
    hasAccess:
      order_information?.companyId !== 1 &&
      !Boolean(order_information?.managerApproval),
    handleCancelledOrder: handleCancelledOrder,
    handleConfirm: handleConfirmOrder("manager"),
    noAccessBannerIndicatorSettings: {
      visible: !managerAccess,
      content:
        "The order is awaiting store manager verification. Once reviewed, it will proceed to procurement approval.",
      textColor: "text-[#4A4A4A]",
      bgColor: "bg-[#AEE1F7]",
    },
  };

  /**
   * procurement approval config
   */
  const procurementApprovalSettings = {
    primaryTitle: "Procurement Approval",
    secondaryTitle:
      "Approval from procurement is necessary to validate budgets, suppliers, and order details before finalizing the request.",
    name: "procurement-approval-settings",
    hasAccess:
      order_information?.companyId !== 1 &&
      Boolean(order_information?.managerApproval) &&
      !Boolean(order_information?.procurementApproval),
    handleCancelledOrder: handleCancelledOrder,
    handleConfirm: handleConfirmOrder("procurement"),
    actionButtonVisibility: procurementAccess,
    noAccessBannerIndicatorSettings: {
      visible: !procurementAccess,
      content:
        "The order is awaiting procurement verification. Once approved, it will proceed to the supplier, who will then determine and set the committed delivery date.",
      textColor: "text-[#4A4A4A]",
      bgColor: "bg-[#AEE1F7]",
    },
  };

  /**
   * supplier approval config
   */

  const supplierApprovalSettings = {
    primaryTitle: "Multim Manager Approval",
    secondaryTitle:
      "A multim manager reviews and authorizes the order to confirm details like pricing, quantity, and compliance before finalization.",
    name: "supplier-approval-config",
    handleCancelledOrder: handleCancelledOrder,
    handleConfirm: handleConfirmOrder("supplier-approve"),

    hasAccess:
      !Boolean(order_information?.multimApproval) &&
      order_information?.companyId === 1,
    actionButtonVisibility: supplierApproval,

    noAccessBannerIndicatorSettings: {
      visible: supplierAccess ?? false,
      content: "Your order is awaiting Multi M Supervisor verification.",
      textColor: "text-[#4A4A4A]",
      bgColor: "bg-[#AEE1F7]",
    },
  };

  /**
   * Multim payment verifiation
   */
  const multimPaymentVerificationApprovalSettings: ActionApprovalProps = {
    primaryTitle: "Payment Verification",
    secondaryTitle:
      "This action will remain pending until the payment has been fully checked and confirmed.",
    name: "multim-payment-verfication",
    hasAccess:
      (supplierAccess &&
        Boolean(order_information?.isPayFirst) &&
        !Boolean(order_information?.isPaid) &&
        Boolean(order_information?.multimApproval)) ??
      false,
    actionButtonVisibility: Boolean(order_information?.payment_detail_image),
    handleConfirm: handleConfirmOrder("supplier-multim-approval"),
    hasAccessBannerIndicatorSettings: {
      visible:
        (supplierAccess && !Boolean(order_information?.payment_detail_image)) ??
        false,
      content: "Waiting for Multi M to upload payment",
      textColor: "text-[#4A4A4A]",
      bgColor: "bg-[#AEE1F7]",
    },
    noAccessBannerIndicatorSettings: {
      visible: !supplierAccess,
      content: "Waiting for supplier verification",
      textColor: "text-[#4A4A4A]",
      bgColor: "bg-[#AEE1F7]",
    },
    customButtons: [
      {
        label: "Reject",
        onClick: handleConfirmOrder("supplier-multim-reject"),
        position: "middle",
      },
    ],
  };

  /**
   * Supplier Staggered Commit
   */
  const supplierStaggeredCommitApprovalSettings: ActionApprovalProps = {
    primaryTitle: "Staggered Delivery Date Setup",
    secondaryTitle:
      "Set up staggered delivery dates to split an order into multiple scheduled shipments, ensuring smoother fulfillment and better inventory management.",
    name: "supplier-staggered-commit",
    hasAccess:
      ((Boolean(order_information?.isPayFirst) &&
        Boolean(order_information?.isPaid) &&
        Boolean(order_information?.multimApproval)) ||
        (!Boolean(order_information?.isPayFirst) &&
          Boolean(order_information?.multimApproval))) &&
      product_batch_data?.length !== 0,
    handleConfirm: handleConfirmOrder("commit"),
    actionButtonVisibility: supplierAccess && Boolean(checkBatchDelivery),
    noAccessBannerIndicatorSettings: {
      visible:
        (!supplierAccess && Boolean(order_information?.multimApproval)) ??
        false,
      content:
        "Waiting for supplier to setup batch delivery date and confirm order",
      textColor: "text-[#4A4A4A]",
      bgColor: "bg-[#AEE1F7]",
    },
    hasAccessBannerIndicatorSettings: {
      visible:
        (Boolean(order_information?.multimApproval) &&
          !Boolean(checkBatchDelivery) &&
          supplierAccess) ??
        false,
      content:
        'Reminder: Click "Setup Batch Delivery" to configure and confirm the batch delivery dates for the staggered delivery setup.',
      textColor: "text-[#4A4A4A]",
      bgColor: "bg-[#AEE1F7]",
    },
  };

  /**
   * This collects all the config objects
   */

  const approvalAccess = [
    managerApprovalSettings,
    supplierApprovalSettings,
    procurementApprovalSettings,
    multimPaymentVerificationApprovalSettings,
    supplierStaggeredCommitApprovalSettings,
  ];

  return (
    <>
      {/**
       * iterate over the mini Sub processes
       *
       *
       * */}
      {approvalAccess
        .filter((fltr) => fltr.hasAccess)
        .map((config) => (
          <>
            <ActionHeader
              primary={config.primaryTitle ?? ""}
              secondary={config.secondaryTitle}
              HeaderIcon={CircleCheck}
            />
            <Approval key={config.name} {...config} />
          </>
        ))}

      {/**
       * Component for committing the order and moving to the next process.
       *
       * @remarks
       * This component is required only during the finalization step.
       */}
      <CommittedDeliveryDate
        name="full-order-committed-delivery"
        hasAccess={
          (Boolean(order_information?.procurementApproval) &&
            Boolean(order_information?.managerApproval)) ||
          (order_information?.companyId === 1 &&
            product_batch_data?.length === 0 &&
            Boolean(order_information?.multimApproval) &&
            ((Boolean(order_information?.isPayFirst) &&
              Boolean(order_information?.isPaid)) ||
              !Boolean(order_information?.isPayFirst)))
        }
        handleCancelledOrder={handleCancelledOrder}
        order_information={order_information}
        handleConfirm={handleConfirmOrder("commit")}
      />

      {/**
       * Component for uploading payment
       *
       * @remarks
       * Renders only when `order_information?.isPayFirst` is true.
       * Prevents rendering when "pay first" is disabled.
       */}

      <MultimUploadPayment
        name="upload-payment"
        hasAccess={
          !supplierAccess &&
          Boolean(order_information?.isPayFirst) &&
          Boolean(order_information?.multimApproval) &&
          !Boolean(order_information?.isPaid)
        }
        order_information={order_information}
        handleConfirm={handleConfirmOrder("multim-upload-payment")}
        noAccessBannerIndicatorSettings={{
          visible: supplierAccess ?? false,
          content: "Order is on hold until payment is received.",
          textColor: "text-[#4A4A4A]",
          bgColor: "bg-[#AEE1F7]",
        }}
        hasAccessBannerIndicatorSettings={{
          visible: !supplierAccess,
          content: Boolean(order_information?.payment_detail_image)
            ? "You can still upload a payment proof as long as it hasn’t been approved by the supplier. Please note that only the latest uploaded image will be recorded."
            : "Please ensure you upload the correct and valid proof of payment to avoid delays in verification and processing.",
          textColor: "text-[#4A4A4A]",
          bgColor: "bg-[#AEE1F7]",
        }}
        actionButtonVisibility={
          !(!uploadedPayment || uploadedPayment.length === 0)
        }
        fileData={{
          files: uploadedPayment,
          handleOpenFileModal,
          handleRemoveFile,
        }}
        primaryTitle="Payment Upload"
        secondaryTitle="Upload and manage payment proofs for verification and processing, as this order follows a Pay First policy and will proceed only after payment has been confirmed."
      />

      <UploadMultipleImageModal
        open={openUploadModal}
        onClose={() => {
          setOpenUploadModal(false);
          setTimeout(() => {
            dispatch(closeModalScroll());
          }, 500);
        }}
        single
        files={uploadedPayment}
        setFiles={handleSetFile}
      />
    </>
  );
}

const MultimUploadPayment = (props: ActionApprovalProps) => {
  /**
   * Destructure props
   */

  const {
    handleConfirm,
    hasAccess,
    hasAccessBannerIndicatorSettings,
    noAccessBannerIndicatorSettings,
    actionButtonVisibility,
    fileData,
    primaryTitle,
    secondaryTitle,
  } = props;

  const { files, handleOpenFileModal, handleRemoveFile } = fileData ?? {};

  if (hasAccess) {
    return (
      <>
        <ActionHeader
          primary={primaryTitle ?? ""}
          secondary={secondaryTitle}
          HeaderIcon={CircleCheck}
        />

        <div className="flex flex-col space-y-2">
          {noAccessBannerIndicatorSettings && (
            <BannerIndicator {...noAccessBannerIndicatorSettings} />
          )}

          {hasAccessBannerIndicatorSettings && (
            <BannerIndicator {...hasAccessBannerIndicatorSettings} />
          )}

          <div className="flex justify-between">
            <div className="w-1/2 space-y-2">
              {hasAccessBannerIndicatorSettings?.visible &&
                (files?.length === 0 || files === null) && (
                  <button
                    onClick={handleOpenFileModal}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium text-sm rounded-xl py-3 px-6 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    Upload Payment
                  </button>
                )}

              {files && files.length > 0 && (
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4"
                      />
                    </svg>
                    <span className="text-sm text-gray-700 font-medium truncate">
                      {files[0].name}
                    </span>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    className="text-gray-400 hover:text-red-500"
                    title="Remove file"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <CancelAndConfirm
              handleConfirm={handleConfirm}
              visible={actionButtonVisibility}
            />
          </div>
        </div>
      </>
    );
  }

  return null;
};

const CommittedDeliveryDate = (props: ActionApprovalProps) => {
  /**
   * Destructure props and order information
   */
  const { handleCancelledOrder, handleConfirm, order_information, hasAccess } =
    props;

  const { procurementApproval, managerApproval, companyId } =
    order_information ?? {};

  const [commitedDeliveryDate, setCommitedDeliveryDate] = useState<
    string | null | undefined
  >(null);

  const supplierAccess = useActionAvailability(5).permission;

  /**
   * Checks if there committed delivery date is valid and is not empty
   */
  const missingDeliveryDate =
    commitedDeliveryDate === null || commitedDeliveryDate === "Invalid Date";

  /**
   * Handles committed delivery date field change
   * @param value value of the field
   */
  const handleCommittedDeliveryChange = (value: Date | null) => {
    if (value !== null) {
      setCommitedDeliveryDate(value.toString());
    }
  };

  /**
   *
   * Cancel, confirm button, and committed delivery date
   * coommitted delivery date is only acceessed by supplier
   *
   * this is only available: if the user has correct access
   *                        if procurement and manager aprroval is approved
   */
  if (hasAccess) {
    return (
      <>
        <ActionHeader
          primary={"Supplier Delivery Date Commit"}
          secondary="The date committed by the supplier for delivering the product to the designated location. This serves as the expected delivery schedule agreed upon between the supplier and buyer."
          HeaderIcon={CheckCircle}
        />

        <BannerIndicator
          visible={!supplierAccess}
          content="Waiting for supplier to setup committed delivery date"
          textColor="text-[#4A4A4A]"
          bgColor="bg-[#AEE1F7]"
        />

        <BannerIndicator
          visible={missingDeliveryDate && supplierAccess}
          content="Don’t forget to set the committed delivery date. It helps us stay on track and deliver on time — just a quick pick!"
          textColor="text-[#A83A3A]"
          bgColor="bg-[#FFEAEA]"
        />

        {supplierAccess && (
          <div className="flex justify-between mt-1">
            <div className="flex flex-col">
              <div className="flex space-x-2">
                <p className="font-semibold">Committed delivery date:</p>
                <button
                  onClick={() => setCommitedDeliveryDate(null)}
                  className="flex text-xs self-center"
                >
                  [ <p className="text-button">Reset</p>]
                </button>
              </div>
              <div className="w-fit">
                <MaterialDateInput
                  readonly={true}
                  colorTheme={"black"}
                  views={["month", "day"]}
                  size="small"
                  minDate={dayjs()}
                  placeholder="Set commited delivery date"
                  value={commitedDeliveryDate}
                  onChange={handleCommittedDeliveryChange}
                />
              </div>
            </div>
            <div className="self-center">
              <CancelAndConfirm
                visible
                disableConfirm={missingDeliveryDate}
                handleCancelledOrder={
                  companyId === 1 ? undefined : handleCancelledOrder
                }
                handleConfirm={() => handleConfirm(commitedDeliveryDate)}
              />
            </div>
          </div>
        )}
      </>
    );
  }
  /**
   * Banner indicator for awaiting for procurement approve
   * This is only visible: if the user has no access on the current process
   *                       if the the order has no committed delivery date
   *                       if the order is approved by store manager
   *                       if the order is approved by procurement
   */
  if (!hasAccess) {
    return (
      <BannerIndicator
        visible={
          missingDeliveryDate &&
          procurementApproval !== null &&
          managerApproval !== null
        }
        content="Your order is awaiting supplier's selection of the committed delivery date. Once the date is set, the order will proceed to dispatch."
        textColor="text-[#4A4A4A]"
        bgColor="bg-[#AEE1F7]"
      />
    );
  }

  return null;
};

const Approval = (props: ActionApprovalProps) => {
  /**
   * Destructure props
   */

  const {
    handleCancelledOrder,
    handleConfirm,
    hasAccess,
    actionButtonVisibility,
    hasAccessBannerIndicatorSettings,
    noAccessBannerIndicatorSettings,
    customButtons,
  } = props;

  console.log(props);

  /**
   *  Cancel and confirm button
   */
  if (hasAccess) {
    return (
      <div className="flex flex-col space-y-2">
        {noAccessBannerIndicatorSettings && (
          <BannerIndicator {...noAccessBannerIndicatorSettings} />
        )}
        {hasAccessBannerIndicatorSettings && (
          <BannerIndicator {...hasAccessBannerIndicatorSettings} />
        )}
        <CancelAndConfirm
          handleCancelledOrder={handleCancelledOrder}
          handleConfirm={handleConfirm}
          visible={actionButtonVisibility}
          customButtons={customButtons}
        />
      </div>
    );
  }

  return null;
};
